import { ShieldCheckIcon } from '@phosphor-icons/react';
import { forwardRef } from 'react';

import { formatOrdinalDate } from '../details-rules';
import { SUBSCRIPTION_INDICATOR, type DeviceDetails } from '../details-schemas';

import styles from './device-metadata-card.module.scss';

export type DeviceMetadataCardProps = {
  details: DeviceDetails;
};

const NOT_SET = '—';

/**
 * The read-only Device Metadata card (US-112437 §6).
 *
 * Two fields shift wording rather than value:
 *
 * - **Warranty** — labelled "Warranty Valid Till" with a date while active; when expired the label
 *   itself becomes "Warranty" and the value reads "Expired".
 * - **Subscription** — the plan name with an inline shield while comfortably active; "Expiring in
 *   30 days" (and no shield) when it is close, and "Expired" in red once it has lapsed. This is the
 *   card the header's Info icon scrolls to, which is why it takes a ref.
 */
export const DeviceMetadataCard = forwardRef<HTMLElement, DeviceMetadataCardProps>(
  function DeviceMetadataCard({ details }, ref) {
    const { hardware, subscriptionIndicator, expired } = details;
    const warrantyExpired = hardware.warrantyExpired;

    const subscriptionValue = expired
      ? 'Expired'
      : subscriptionIndicator === SUBSCRIPTION_INDICATOR.EXPIRING_SOON
        ? 'Expiring in 30 days'
        : (hardware.subscriptionPlan ?? NOT_SET);

    return (
      <section className={styles.card} ref={ref} aria-label="Device metadata">
        <h2 className={styles.title}>Device Metadata</h2>

        <dl className={styles.grid}>
          <div className={styles.field}>
            <dt className={styles.label}>Type</dt>
            <dd className={styles.value}>{hardware.category ?? NOT_SET}</dd>
          </div>

          <div className={styles.field}>
            <dt className={styles.label}>Serial No</dt>
            <dd className={styles.value}>{hardware.serialNumber ?? NOT_SET}</dd>
          </div>

          <div className={styles.field}>
            <dt className={styles.label}>{warrantyExpired ? 'Warranty' : 'Warranty Valid Till'}</dt>
            <dd className={warrantyExpired ? styles.valueDanger : styles.value}>
              {warrantyExpired
                ? 'Expired'
                : (formatOrdinalDate(hardware.warrantyValidTill) ?? NOT_SET)}
            </dd>
          </div>

          <div className={styles.field}>
            <dt className={styles.label}>Subscription</dt>
            <dd className={expired ? styles.valueDanger : styles.value}>
              {!expired && subscriptionIndicator === SUBSCRIPTION_INDICATOR.PROTECTED ? (
                <ShieldCheckIcon size={16} weight="fill" aria-hidden className={styles.shield} />
              ) : null}
              {subscriptionValue}
            </dd>
          </div>
        </dl>
      </section>
    );
  },
);
